/**
 * Betalen door landschappen aan te wijzen.
 *
 * In dit spel liggen grondstoffen op de landschappen, dus betalen is een handeling:
 * je klikt aan wélke er afgaan. Dat is precies wat het origineel deed ("Je betaalt de
 * bouwkosten met een klik op een landschap met een grondstofsymbool", handboek p. 14),
 * en het doet ertoe zodra je meer van een soort hebt dan je nodig hebt — dan bepaal je
 * zelf welk landschap leegloopt.
 *
 * Deze rekenkant staat los van het scherm zodat hij te testen is.
 */
import { LANDSCAPE_RESOURCE, type CardCost, type Resource } from '@catan/contracts';
import type { PlayerState, Side } from '@catan/rules';

export interface TileRef {
  readonly column: number;
  readonly side: Side;
}

/**
 * Wat een handeling kost.
 *
 * Twee vormen, want het spel kent ze allebei. Een straat kost één hout en één klei: dan
 * staat per soort vast hoeveel er af moet. Een stapel doorkijken kost twee grondstoffen
 * **naar keuze**: dan telt alleen het aantal. Zonder dat tweede geval moest de tweede
 * zichzelf betalen, en dan klik je niets aan, zie je niets wegvliegen en hoor je niets.
 */
export type Kosten =
  | { readonly soort: 'vast'; readonly cost: CardCost }
  | { readonly soort: 'vrij'; readonly aantal: number };

/** Kortere schrijfwijze voor de gewone, vaste kosten van een kaart of bouwwerk. */
export const vast = (cost: CardCost): Kosten => ({ soort: 'vast', cost });
/** Zoveel grondstoffen, maakt niet uit welke. */
export const vrij = (aantal: number): Kosten => ({ soort: 'vrij', aantal });

/**
 * Wat er van de kosten nog openstaat, per soort.
 *
 * Alleen zinnig bij vaste kosten; bij vrije kosten is er geen soort die openstaat, alleen
 * een aantal — daarvoor is `resterendAantal`.
 */
export function resterendeKosten(
  player: PlayerState,
  kosten: Kosten,
  betaald: readonly TileRef[],
): Map<Resource, number> {
  const rest = new Map<Resource, number>();
  if (kosten.soort !== 'vast') return rest;
  for (const [soort, aantal] of Object.entries(kosten.cost) as [Resource, number][]) {
    rest.set(soort, aantal);
  }
  for (const ref of betaald) {
    const tile = tileVan(player, ref);
    if (tile === undefined) continue;
    const soort = LANDSCAPE_RESOURCE[tile.landscape];
    rest.set(soort, (rest.get(soort) ?? 0) - 1);
  }
  return rest;
}

/** Hoeveel grondstoffen er nog aangewezen moeten worden, ongeacht de soort. */
export function resterendAantal(
  player: PlayerState,
  kosten: Kosten,
  betaald: readonly TileRef[],
): number {
  if (kosten.soort === 'vrij') return Math.max(0, kosten.aantal - betaald.length);
  return [...resterendeKosten(player, kosten, betaald).values()]
    .filter((n) => n > 0)
    .reduce((a, b) => a + b, 0);
}

/** Is alles betaald? */
export function volledigBetaald(
  player: PlayerState,
  kosten: Kosten,
  betaald: readonly TileRef[],
): boolean {
  return resterendAantal(player, kosten, betaald) === 0;
}

/**
 * Mag je dit landschap (nog een keer) aanwijzen?
 *
 * Nee als de soort al genoeg is aangewezen, en nee als er niet genoeg op ligt. Bij vrije
 * kosten vervalt die eerste vraag: elke soort is goed, zolang er nog wat te betalen valt.
 */
export function magAanwijzen(
  player: PlayerState,
  kosten: Kosten,
  betaald: readonly TileRef[],
  doel: TileRef,
): { ok: true } | { ok: false; reden: string } {
  const tile = tileVan(player, doel);
  if (tile === undefined) return { ok: false, reden: 'dat landschap bestaat niet' };

  if (kosten.soort === 'vast') {
    const soort = LANDSCAPE_RESOURCE[tile.landscape];
    const rest = resterendeKosten(player, kosten, betaald);
    if ((rest.get(soort) ?? 0) <= 0) {
      return { ok: false, reden: `van ${soort} heb je al genoeg aangewezen` };
    }
  } else if (betaald.length >= kosten.aantal) {
    return { ok: false, reden: 'je hebt al genoeg aangewezen' };
  }
  const alGebruikt = betaald.filter((r) => r.column === doel.column && r.side === doel.side).length;
  if (alGebruikt >= tile.resources) return { ok: false, reden: 'daar ligt niet meer' };
  return { ok: true };
}

/** De landschappen die op dit moment nog van pas komen — die lichten op op het bord. */
export function bruikbareLandschappen(
  player: PlayerState,
  kosten: Kosten,
  betaald: readonly TileRef[],
): TileRef[] {
  return player.principality.landscapes
    .filter((t) => magAanwijzen(player, kosten, betaald, { column: t.column, side: t.side }).ok)
    .map((t) => ({ column: t.column, side: t.side }));
}

function tileVan(player: PlayerState, ref: TileRef) {
  return player.principality.landscapes.find(
    (t) => t.column === ref.column && t.side === ref.side,
  );
}
